// Chart options of the Resource & capacity panels (SPEC-0052 v1, C3): what each panel already
// proposes (chart type, measure, group-by), kept per panel in the user's saved document.

import { PANEL_IDS } from "./analyticsLayout";

// The options a panel may save; anything else in a saved document is dropped.
// `display` is the Chart / Grid toggle of the Scenario usage panel (SPEC-0060 §4.2).
export const CHART_OPTION_KEYS = ["chartType", "valueField", "columnField", "display"];
// Group by on several fields (SPEC-0053): an ordered list of field keys, the first the outer level.
export const GROUP_BY_KEY = "groupBy";
export const MAX_GROUP_BY = 3;
// In-chart filters (SPEC-0053 v2): { [fieldKey]: valueId[] }, kept across sessions with the rest.
export const FILTERS_KEY = "filters";
/** Overlay lines (capacity, availability, approved budget, Others) the user hid from a chart. */
export const HIDDEN_OVERLAYS_KEY = "hiddenOverlays";
const MAX_FILTER_FIELDS = 50;
const MAX_FILTER_VALUES = 500;

const MAX_LENGTH = 64;

// The chart types Syncfusion's pivot chart offers on the pivot panels (Approved Budget, Team
// Availability); a saved type outside this list is ignored.
export const PIVOT_CHART_TYPES = [
  "Column", "Bar", "Line", "Spline", "Area", "SplineArea", "StepLine", "StepArea",
  "StackingColumn", "StackingBar", "StackingArea", "StackingColumn100", "StackingBar100",
  "StackingArea100", "StackingLine", "StackingSpline", "Scatter", "Bubble", "Polar", "Radar",
  "Pareto", "Pie", "Doughnut", "Funnel", "Pyramid",
];

const validOption = (v) => typeof v === "string" && v.length > 0 && v.length <= MAX_LENGTH;

const sanitizeFilters = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out = {};
  Object.entries(raw)
    .slice(0, MAX_FILTER_FIELDS)
    .forEach(([name, items]) => {
      if (!validOption(name) || !Array.isArray(items)) return;
      const list = Array.from(new Set(items.filter((v) => typeof v === "string"))).slice(0, MAX_FILTER_VALUES);
      if (list.length > 0) out[name] = list;
    });
  return Object.keys(out).length > 0 ? out : null;
};

/**
 * { [panelId]: { chartType?, valueField?, columnField?, groupBy?, hiddenOverlays?, filters? } } with known panels
 * (the typology's ids), string options, a groupBy list of at most MAX_GROUP_BY distinct strings,
 * and filters as lists of strings per field.
 */
export const sanitizeChartOptions = (raw, panelIds = PANEL_IDS) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  panelIds.forEach((id) => {
    const panel = raw[id];
    if (!panel || typeof panel !== "object") return;
    const kept = {};
    CHART_OPTION_KEYS.forEach((key) => {
      const v = panel[key];
      if (validOption(v)) kept[key] = v;
    });
    const groupBy = panel[GROUP_BY_KEY];
    if (Array.isArray(groupBy)) {
      const list = Array.from(new Set(groupBy.filter(validOption))).slice(0, MAX_GROUP_BY);
      if (list.length > 0) kept[GROUP_BY_KEY] = list;
    }
    const hiddenOverlays = panel[HIDDEN_OVERLAYS_KEY];
    if (Array.isArray(hiddenOverlays)) {
      const list = Array.from(new Set(hiddenOverlays.filter(validOption)));
      if (list.length > 0) kept[HIDDEN_OVERLAYS_KEY] = list;
    }
    const filters = sanitizeFilters(panel[FILTERS_KEY]);
    if (filters) kept[FILTERS_KEY] = filters;
    if (Object.keys(kept).length > 0) out[id] = kept;
  });
  return out;
};
